import { useState, useEffect, useRef } from "react";
import { AiOutlineSearch } from "react-icons/ai";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { IoIosArrowDown } from "react-icons/io";
import { useDispatch, useSelector } from "react-redux";
import { logout } from "../../redux/actions/UserAction";
import { getAllCity } from "../../redux/actions/CityAction";
import useGetAllCity from "../../hooks/getCityList";
import logo from "../../assets/logo/logo.png";

const Header = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchValue, setSearchValue] = useState("");
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isUserDropdownOpen, setIsUserDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const navbarRef = useRef(null);

  // Track which dropdown is currently open
  const [openDropdown, setOpenDropdown] = useState(null);
  const [openSubDropdown, setOpenSubDropdown] = useState(null);
  const [openDoubleSubDropdown, setOpenDoubleSubDropdown] = useState(null);

  const { userDetails, isAuthenticated } = useSelector((state) => state.user);
  const { citylist: cities } = useGetAllCity();

  // Define all menu items in the exact sequence you want
  const menuItems = [
    // Static link
    {
      type: 'link',
      title: "Home",
      link: "/"
    },
    // Dropdown menu
    {
      type: 'dropdown',
      title: "City",
      items: cities?.map(city => ({
        name: city.name,
        link: `leagueCity/${city.name}`
      }))
    },
    // Dropdown menu
    {
      type: 'dropdown',
      title: "Activities",
      items: [
        {
          name: "Leagues",
          nested: {
            title: "Leagues",
            items: [
              {
                name: "Leagues Details",
                nested: {
                  title: "Leagues Details",
                  items: [
                    { name: "3x3 Recreational", link: "/3x3-recreational" },
                    { name: "5x5 Recreational", link: "/5x5-recreational" }
                  ]
                }
              },
              { name: "Leagues Live", link: "leagues-score" },
              { name: "League Teams", link: "league-teams" },
              { name: "Past Leagues", link: "past-leagues" }
            ]
          }
        },
        { name: "Tournament", link: "tournament-details" },
        { name: "Camp", link: "camp-details" },
        { name: "Training", link: "training-program-details" },
        { name: "Clinics", link: "clinics" },
        {
          name: "Competitive",
          nested: {
            title: "Competitive",
            items: [
              { name: "3x3 Pro", link: "/comp3x3tryout" },
              { name: "5x5 Competitive", link: "/comp5x5tryout" }
            ]
          }
        }
      ]
    },
    // Dropdown menu
    {
      type: 'dropdown',
      title: "Tournament",
      items: [
        { name: "Tournament Details", link: "tournament-details" },
        { name: "Live Tournament", link: "tournamentsScore" },
        { name: "Tournament Teams", link: "tournament-teams-by-agegroup" },
        { name: "Past Tournaments", link: "past-tournaments" }
      ]
    },
    // Dropdown menu
    {
      type: 'dropdown',
      title: "News",
      items: [
        { name: "News", link: "news" },
        { name: "Vlog", link: "vlog" },
        { name: "Blog", link: "blog" }
      ]
    },
    // Dropdown menu
    {
      type: 'dropdown',
      title: "ContactUs",
      items: [
        { name: "Contact Us", link: "contact" },
        { name: "SignUp As Partner", link: "signuppartner" }
      ]
    },
    // Dropdown menu
    {
      type: 'dropdown',
      title: "Players",
      items: [
        { name: "3x3 Players", link: "players" },
        { name: "5x5 Players", link: "5x5players" },
        { name: "Competitive 3x3 Players", link: "comptetive3x3players" },
        { name: "Competitive 5x5 Players", link: "comptetive5x5players" }
      ]
    },
    // Static link
    {
      type: 'link',
      title: "Rules",
      link: "rules"
    },
    // Static link
    {
      type: 'link',
      title: "Events",
      link: "events"
    },
    // Static link
    {
      type: 'link',
      title: "About",
      link: "about"
    },
    {
      type: 'link',
      title: "Parties",
      link: "https://shopclient.sfbayareabasketball.com"
    }
  ];

  // Handle search
  const handleSearch = (e) => {
    if (e.key === "Enter") {
      navigate(`/store/${searchValue}`);
    }
  };

  // User logout
  const logoutUser = () => {
    dispatch(logout());
    setIsUserDropdownOpen(false);
    navigate("/login");
  };

  // Close all dropdowns
  const closeAllDropdowns = () => {
    setOpenDropdown(null);
    setOpenSubDropdown(null);
    setOpenDoubleSubDropdown(null);
    setIsUserDropdownOpen(false);
  };

  // Toggle mobile menu
  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
    if (isMobileMenuOpen) closeAllDropdowns();
  };

  // Handle dropdown hover (desktop)
  const handleDropdownHover = (menuName) => {
    if (window.innerWidth > 1024) {
      setOpenDropdown(menuName);
      setOpenSubDropdown(null);
      setOpenDoubleSubDropdown(null);
    }
  };

  // Handle subdropdown hover (desktop)
  const handleSubDropdownHover = (submenuName) => {
    if (window.innerWidth > 1024) {
      setOpenSubDropdown(submenuName);
      setOpenDoubleSubDropdown(null);
    }
  };

  // Handle Double subdropdown hover (desktop)
  const handleDoubleSubDropdownHover = (submenuName) => {
    if (window.innerWidth > 1024) {
      setOpenDoubleSubDropdown(submenuName);
    }
  };

  // Handle dropdown click (mobile)
  const handleDropdownClick = (menuName) => {
    if (window.innerWidth <= 1024) {
      setOpenDropdown(openDropdown === menuName ? null : menuName);
      setOpenSubDropdown(null);
      setOpenDoubleSubDropdown(null);
    }
  };

  // Close all dropdowns when leaving the navbar
  const handleNavbarLeave = () => {
    if (window.innerWidth > 1024) {
      closeAllDropdowns();
    }
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        closeAllDropdowns();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Fetch cities on mount
  useEffect(() => {
    dispatch(getAllCity());
  }, [dispatch]);

  return (
    <nav ref={navbarRef} className="fixed bg-[#181b22] h-[80px] z-[1000] w-full">
      <div ref={dropdownRef} className="relative w-full text-white text-md mt-0 md:mt-[15px]">
        <div className="relative w-full px-[25px] mt-[15px] lg:mt-0">
          <div className="flex w-full items-center justify-between gap-2">
            {/* Mobile menu button */}
            <div className="lg:hidden flex items-center">
              <button onClick={toggleMobileMenu}>
                {isMobileMenuOpen ? (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="w-6 h-6 text-white"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                      clipRule="evenodd"
                    />
                  </svg>
                ) : (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="w-6 h-6 text-white"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M4 6h16M4 12h16M4 18h16"
                    />
                  </svg>
                )}
              </button>
            </div>

            {/* Logo */}
            <div className="max-w-[250px] max-h-[60px]">
              <Link to="/" className="max-w-[350px] h-fit hidden lg:block gap-1 font-bold justify-center items-center">
                <img
                  src={logo}
                  alt="sfbaybasketballarea"
                  className="w-full max-h-[60px] object-cover"
                />
              </Link>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex gap-1 items-center">
              {/* Search */}
              <div className="hidden xl:flex flex-1 border-b-2 border-b-indigo-50">
                <input
                  placeholder="Search"
                  onKeyPress={handleSearch}
                  value={searchValue}
                  onChange={(e) => setSearchValue(e.target.value)}
                  className="flex-1 text-sm px-2 bg-transparent focus:outline-none"
                />
                <Link to={`/store/${searchValue}`} className="cursor-pointer">
                  <AiOutlineSearch size={30} className="cursor-pointer" />
                </Link>
              </div>

              {/* Render all menu items in the specified sequence */}
              {menuItems.map((menuItem, index) => {
                if (menuItem.type === 'link') {
                  return (
                    <NavLink
                      key={index}
                      to={menuItem.link}
                      className="px-4 py-2 text-[15px] hover:text-red-400 whitespace-nowrap"
                    >
                      {menuItem.title}
                    </NavLink>
                  );
                } else if (menuItem.type === 'dropdown') {
                  return (
                    <div
                      key={index}
                      className="relative group"
                      onMouseEnter={() => handleDropdownHover(index)}
                      onMouseLeave={handleNavbarLeave}
                    >
                      <button
                        className="flex items-center text-white cursor-pointer gap-2 px-4 py-2"
                      >
                        <span className="text-[15px]">{menuItem.title}</span>
                        <IoIosArrowDown
                          size={20}
                          className={`transition-transform duration-300 ${openDropdown === index ? "rotate-180" : ""
                            }`}
                        />
                      </button>

                      {/* Main Dropdown */}
                      {openDropdown === index && (
                        <div
                          className="absolute left-0 mt-0 pt-2 bg-transparent z-10 min-w-[200px]"
                          onMouseLeave={handleNavbarLeave}>
                          <div className="bg-white border rounded shadow-lg">
                            <ul>
                              {menuItem.items.map((item, itemIndex) => (
                                <li key={itemIndex} className="relative hover:bg-gray-50"
                                  onMouseEnter={() => {
                                    if (item.nested) {
                                      handleSubDropdownHover(`${index}-${itemIndex}`);
                                    } else {
                                      setOpenSubDropdown(null);
                                      setOpenDoubleSubDropdown(null);
                                    }
                                  }}
                                >
                                  <Link
                                    to={item.link || '#'}
                                    className="flex justify-between items-center px-4 py-1 text-gray-800"
                                  >
                                    <span>{item.name}</span>
                                    {item.nested && (
                                      <IoIosArrowDown
                                        size={16}
                                        className={`ml-2 transition-transform duration-300 ${openSubDropdown === `${index}-${itemIndex}` ? "rotate-180" : ""
                                          }`}
                                      />
                                    )}
                                  </Link>

                                  {/* Nested Dropdown */}
                                  {item.nested && openSubDropdown === `${index}-${itemIndex}` && (
                                    <div
                                      className="absolute left-full top-0 ml-0 pl-1 bg-transparent z-20"
                                      onMouseLeave={handleNavbarLeave}
                                    >
                                      <div className="bg-white border rounded shadow-lg min-w-[200px]">
                                        <ul>
                                          {item.nested.items.map((nestedItem, nestedIndex) => (
                                            <li
                                              key={nestedIndex}
                                              className="relative hover:bg-gray-50"
                                              onMouseEnter={() => {
                                                if (nestedItem.nested) {
                                                  handleDoubleSubDropdownHover(`${index}-${itemIndex}-${nestedIndex}`);
                                                } else {
                                                  setOpenDoubleSubDropdown(null);
                                                }
                                              }}
                                            >
                                              <Link
                                                to={nestedItem.link || '#'}
                                                className="flex justify-between items-center px-4 py-1 text-gray-800"
                                              >
                                                <span>{nestedItem.name}</span>
                                                {nestedItem.nested && (
                                                  <IoIosArrowDown
                                                    size={16}
                                                    className={`ml-2 transition-transform duration-300 ${openDoubleSubDropdown === `${index}-${itemIndex}-${nestedIndex}` ? "rotate-180" : ""
                                                      }`}
                                                  />
                                                )}
                                              </Link>

                                              {/* Double Nested Dropdown */}
                                              {nestedItem.nested && openDoubleSubDropdown === `${index}-${itemIndex}-${nestedIndex}` && (
                                                <div
                                                  className="absolute left-full top-0 ml-0 pl-1 bg-transparent z-30"
                                                  onMouseLeave={handleNavbarLeave}
                                                >
                                                  <div className="bg-white border rounded shadow-lg min-w-[200px]">
                                                    <ul>
                                                      {nestedItem.nested.items.map((doubleNestedItem, doubleNestedIndex) => (
                                                        <li
                                                          key={doubleNestedIndex}
                                                          className="hover:bg-gray-50"
                                                        >
                                                          <Link
                                                            to={doubleNestedItem.link}
                                                            className="block px-4 py-1 text-gray-800"
                                                          >
                                                            {doubleNestedItem.name}
                                                          </Link>
                                                        </li>
                                                      ))}
                                                    </ul>
                                                  </div>
                                                </div>
                                              )}
                                            </li>
                                          ))}
                                        </ul>
                                      </div>
                                    </div>
                                  )}
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                }
                return null;
              })}

              {/* User Section */}
              <div className="flex items-center gap-2">
                {/* Mobile Search Button */}
                <div className="lg:hidden flex items-center justify-end">
                  <button
                    className="text-white"
                    onClick={() => setIsSearchOpen(!isSearchOpen)}
                  >
                    <AiOutlineSearch size={24} />
                  </button>
                </div>

                {isAuthenticated ? (
                  <div className="relative">
                    <div
                      className="flex items-center gap-1"
                      onMouseEnter={() => window.innerWidth > 1024 && setIsUserDropdownOpen(true)}
                      onMouseLeave={() => window.innerWidth > 1024 && setIsUserDropdownOpen(false)}
                    >
                      <button
                        className="w-[50px] h-[50px] rounded-full overflow-hidden"
                        onClick={() => window.innerWidth <= 1024 && setIsUserDropdownOpen(!isUserDropdownOpen)}
                      >
                        <img
                          className="w-full h-full object-cover"
                          src="https://media.istockphoto.com/id/1337144146/vector/default-avatar-profile-icon-vector.jpg?s=612x612&w=0&k=20&c=BIbFwuv7FxTWvh5S3vB6bkT0Qv8Vn8N5Ffseq84ClGI="
                          alt="User Avatar"
                        />
                      </button>

                      {/* Add this invisible padding element to create a hoverable bridge */}
                      {window.innerWidth > 1024 && isUserDropdownOpen && (
                        <div className="absolute top-full h-2 w-full bg-transparent" />
                      )}
                    </div>

                    {isUserDropdownOpen && (
                      <div
                        className="origin-top-right absolute right-0 mt-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5"
                        onMouseEnter={() => window.innerWidth > 1024 && setIsUserDropdownOpen(true)}
                        onMouseLeave={() => window.innerWidth > 1024 && setIsUserDropdownOpen(false)}
                      >
                        <div className="py-1">
                          <NavLink
                            to="/profile"
                            className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                            onClick={closeAllDropdowns}
                          >
                            Profile
                          </NavLink>
                          <div className="block px-4 py-2 text-sm text-gray-700">
                            {userDetails.name}
                          </div>
                          <div className="block px-4 py-2 text-sm text-gray-700">
                            {userDetails.email}
                          </div>
                          <NavLink
                            to="/order"
                            className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                            onClick={closeAllDropdowns}
                          >
                            Order
                          </NavLink>
                          <NavLink
                            to="/cart"
                            className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                            onClick={closeAllDropdowns}
                          >
                            Cart
                          </NavLink>
                          <button
                            className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                            onClick={logoutUser}
                          >
                            Logout
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <NavLink
                    to="/login"
                    className="border-2 text-[12px] p-2 hover:opacity-80 bg-[#fec749] text-black border-[#fec749] rounded-md"
                  >
                    Login/Register
                  </NavLink>
                )}
              </div>
            </div>

            {/* Mobile Search Input */}
            {isSearchOpen && (
              <div className="lg:hidden absolute top-full left-0 right-0 bg-[#181b22] p-4">
                <div className="relative">
                  <input
                    placeholder="Search"
                    onKeyPress={handleSearch}
                    value={searchValue}
                    onChange={(e) => setSearchValue(e.target.value)}
                    className="w-full px-4 py-2 rounded bg-gray-500 bg-opacity-20 text-white"
                  />
                  <button
                    onClick={() => setIsSearchOpen(false)}
                    className="absolute right-2 top-1/2 transform -translate-y-1/2 text-white"
                  >
                    <AiOutlineSearch size={24} />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Mobile Navigation Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden fixed z-40 w-full mt-[80px] bg-gray-100 overflow-y-auto max-h-[calc(100vh-80px)]">
            <div className="px-3 py-4">
              <div className="flex flex-col gap-2 font-bold tracking-wider">
                {/* Render all menu items in the specified sequence */}
                {menuItems.map((menuItem, index) => {
                  if (menuItem.type === 'link') {
                    return (
                      <NavLink
                        key={index}
                        to={menuItem.link}
                        className="px-4 py-2 text-[15px] hover:text-red-400 text-black"
                        onClick={closeAllDropdowns}
                      >
                        {menuItem.title}
                      </NavLink>
                    );
                  } else if (menuItem.type === 'dropdown') {
                    return (
                      <div key={index} className="relative">
                        <button
                          className="flex items-center text-black cursor-pointer gap-2 px-4 py-2 w-full text-left"
                          onClick={() => handleDropdownClick(index)}
                        >
                          <span className="text-[15px]">{menuItem.title}</span>
                          <IoIosArrowDown
                            size={20}
                            className={`transition-transform duration-300 ${openDropdown === index ? "rotate-180" : ""
                              }`}
                          />
                        </button>

                        {openDropdown === index && (
                          <div className="ml-4 mt-2 bg-white rounded shadow-inner">
                            <ul>
                              {menuItem.items.map((item, itemIndex) => (
                                <li key={itemIndex} className="relative">
                                  <button
                                    className="flex items-center justify-between w-full px-4 py-2 text-gray-800 hover:bg-gray-100 text-left"
                                    onClick={() => {
                                      if (item.nested) {
                                        setOpenSubDropdown(
                                          openSubDropdown === `${index}-${itemIndex}`
                                            ? null
                                            : `${index}-${itemIndex}`
                                        );
                                      } else {
                                        navigate(item.link || '#');
                                        closeAllDropdowns();
                                      }
                                    }}
                                  >
                                    <span>{item.name}</span>
                                    {item.nested && (
                                      <IoIosArrowDown
                                        size={16}
                                        className={`transition-transform duration-300 ${openSubDropdown === `${index}-${itemIndex}`
                                          ? "rotate-180"
                                          : ""
                                          }`}
                                      />
                                    )}
                                  </button>

                                  {item.nested && openSubDropdown === `${index}-${itemIndex}` && (
                                    <div className="ml-4 bg-gray-50 rounded">
                                      <ul>
                                        {item.nested.items.map((nestedItem, nestedIndex) => (
                                          <li key={nestedIndex} className="relative">
                                            <button
                                              className="flex items-center justify-between w-full px-4 py-2 text-gray-800 hover:bg-gray-100 text-left"
                                              onClick={() => {
                                                if (nestedItem.nested) {
                                                  setOpenSubDropdown(
                                                    openSubDropdown === `${index}-${itemIndex}-${nestedIndex}`
                                                      ? null
                                                      : `${index}-${itemIndex}-${nestedIndex}`
                                                  );
                                                } else {
                                                  navigate(nestedItem.link || '#');
                                                  closeAllDropdowns();
                                                }
                                              }}
                                            >
                                              <span>{nestedItem.name}</span>
                                              {nestedItem.nested && (
                                                <IoIosArrowDown
                                                  size={16}
                                                  className={`transition-transform duration-300 ${openSubDropdown === `${index}-${itemIndex}-${nestedIndex}`
                                                    ? "rotate-180"
                                                    : ""
                                                    }`}
                                                />
                                              )}
                                            </button>

                                            {nestedItem.nested && openSubDropdown === `${index}-${itemIndex}-${nestedIndex}` && (
                                              <div className="ml-4 bg-gray-100 rounded">
                                                <ul>
                                                  {nestedItem.nested.items.map((doubleNestedItem, doubleNestedIndex) => (
                                                    <li key={doubleNestedIndex}>
                                                      <Link
                                                        to={doubleNestedItem.link}
                                                        className="block px-4 py-2 text-gray-800 hover:bg-gray-200"
                                                        onClick={closeAllDropdowns}
                                                      >
                                                        {doubleNestedItem.name}
                                                      </Link>
                                                    </li>
                                                  ))}
                                                </ul>
                                              </div>
                                            )}
                                          </li>
                                        ))}
                                      </ul>
                                    </div>
                                  )}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    );
                  }
                  return null;
                })}
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Header;